import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaShoppingCart, FaEye } from 'react-icons/fa';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import { products } from '../data/productsData';
import { addToCart } from '../utils/cartUtils';
import { getProductDisplayInfo } from '../utils/productUtils';
import { useToast } from './ToastProvider';
import FeaturedProductsCarousel from './FeaturedProductsCarousel';
import CategoriesSection from './CategoriesSection';
import './ProductsSection.css';


const ProductsSection = () => {
  const [addedItems, setAddedItems] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const productsGridRef = useRef(null);
  const productsTitleRef = useRef(null);
  const navigate = useNavigate();
  const { showToast } = useToast();

  // All products (excluding Custom Orders category)
  const allProducts = products.filter(product =>
    product.category !== "Custom Orders" && product.orderType !== "whatsapp"
  );

  // Pagination settings - responsive
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const productsPerPageDesktop = 8;
  const productsPerPageMobile = 4;
  const productsPerPage = windowWidth < 768 ? productsPerPageMobile : productsPerPageDesktop;

  // Calculate pagination
  const totalPages = Math.ceil(allProducts.length / productsPerPage);
  const startIndex = (currentPage - 1) * productsPerPage;
  const endIndex = startIndex + productsPerPage;
  const paginatedProducts = useMemo(() => {
    return allProducts.slice(startIndex, endIndex);
  }, [startIndex, endIndex, allProducts]);

  // Reset to page 1 when products per page changes
  useEffect(() => {
    setCurrentPage(1);
  }, [productsPerPage]);

  const handlePageChange = (page) => {
    setCurrentPage(page);

    setTimeout(() => {
      if (productsTitleRef.current) {
        const headerOffset = 120;
        const elementPosition =
          productsTitleRef.current.getBoundingClientRect().top;
        const offsetPosition =
          elementPosition + window.pageYOffset - headerOffset;

        window.scrollTo({
          top: offsetPosition,
          behavior: "smooth",
        });
      }
    }, 0);
  };

  const handleAddToCart = (product, e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      // Determine default variant if applicable
      const { lowestPriceVariant } = getProductDisplayInfo(product);

      // Add to cart with lowest price variant (quantity 1)
      addToCart(product, lowestPriceVariant, 1);

      setAddedItems({ ...addedItems, [product.id]: true });
      window.dispatchEvent(new Event('cartUpdated'));
      showToast('Added to cart', 'success');

      setTimeout(() => {
        setAddedItems({ ...addedItems, [product.id]: false });
      }, 1000);
    } catch (err) {
      showToast('Could not add to cart', 'error');
    }
  };

  const handleViewProduct = (product, e) => {
    e.preventDefault();
    e.stopPropagation();
    navigate(`/product/${product.slug}`);
  };

  const ProductCard = ({ product, index, isCarousel = false }) => {
    // Determine display price and image using utility
    const { displayPrice, displayImage } = getProductDisplayInfo(product);

    return (
      <motion.div
        className={`product-card ${isCarousel ? 'carousel-card' : ''}`}
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: index * 0.1 }}
        whileHover={{ scale: 1.02, y: -5 }}
      >
        <Link to={`/product/${product.slug}`} className="product-link">
          <div className="product-image-wrapper">
            <img src={displayImage} alt={product.name} className="product-image" loading="lazy" decoding="async" />
          </div>
          <div className="product-info">
            <h3>{product.name}</h3>
            <div className="product-price">₹ {displayPrice}</div>
          </div>
        </Link>
        <div className="product-actions">
          <motion.button
            className="btn-add-cart"
            onClick={(e) => handleAddToCart(product, e)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            animate={addedItems[product.id] ? { scale: [1, 1.2, 1] } : {}}
          >
            <FaShoppingCart />
            {addedItems[product.id] ? 'Added!' : 'Add to Cart'}
          </motion.button>
          <motion.button
            className="btn-view"
            onClick={(e) => handleViewProduct(product, e)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <FaEye />
            View
          </motion.button>
        </div>
      </motion.div>
    );
  };

  return (
    <section className="products-section" id="products">
      <div className="container">
        {/* Categories Section */}
        <CategoriesSection />

        {/* Aceternity-Style Featured Products Carousel */}
        <FeaturedProductsCarousel products={allProducts} limit={6} />


        {/* Products Grid Section */}
        <div className="products-grid-section" ref={productsGridRef}>
          <div className="home-section-header">
            <motion.h2
              ref={productsTitleRef}
              className="section-title"
              initial={{ opacity: 0, y: -20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              Our Products
            </motion.h2>
            <motion.p
              className="section-subtitle"
              initial={{ opacity: 0, y: -20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              style={{ margin: 0 }}
            >
              Explore our handpicked collection of premium handcrafted resin art and decor
            </motion.p>
          </div>

          {/* Products Grid */}
          <div className="products-grid">
            {paginatedProducts.map((product, index) => (
              <ProductCard key={product.id} product={product} index={index} />
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="products-pagination">
              <button
                type="button"
                className="pagination-arrow"
                onClick={() => {
                  if (currentPage > 1) {
                    handlePageChange(currentPage - 1);
                  }
                }}
                disabled={currentPage === 1}
                aria-label="Previous page"
              >
                <IconChevronLeft size={20} />
              </button>

              <div className="pagination-numbers">
                {(() => {
                  const getPageNumbers = () => {
                    const pages = [];
                    // Always show all pages if total pages are 7 or less
                    if (totalPages <= 7) {
                      for (let i = 1; i <= totalPages; i++) {
                        pages.push(i);
                      }
                    } else {
                      // Always show first page
                      pages.push(1);

                      if (currentPage > 3) {
                        pages.push('...');
                      }

                      // Show current page and neighbors
                      let start = Math.max(2, currentPage - 1);
                      let end = Math.min(totalPages - 1, currentPage + 1);

                      // Adjust if close to beginning
                      if (currentPage <= 3) {
                        start = 2;
                        end = 4;
                      }

                      // Adjust if close to end
                      if (currentPage >= totalPages - 2) {
                        start = totalPages - 3;
                        end = totalPages - 1;
                      }

                      for (let i = start; i <= end; i++) {
                        pages.push(i);
                      }

                      if (currentPage < totalPages - 2) {
                        pages.push('...');
                      }

                      // Always show last page
                      pages.push(totalPages);
                    }
                    return pages;
                  };

                  return getPageNumbers().map((page, index) => (
                    <button
                      key={index}
                      type="button"
                      className={`pagination-number ${currentPage === page ? 'active' : ''} ${page === '...' ? 'dots' : ''}`}
                      onClick={() => typeof page === 'number' && handlePageChange(page)}
                      disabled={page === '...'}
                      aria-label={page === '...' ? 'More pages' : `Go to page ${page}`}
                    >
                      {page}
                    </button>
                  ));
                })()}
              </div>

              <button
                type="button"
                className="pagination-arrow"
                onClick={() => {
                  if (currentPage < totalPages) {
                    handlePageChange(currentPage + 1);
                  }
                }}
                disabled={currentPage === totalPages}
                aria-label="Next page"
              >
                <IconChevronRight size={20} />
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default ProductsSection;

